import { useMemo } from 'react'

import { useModals, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import type { PageContextPayload } from '@contexts/pageContext'
import PageContext from '@contexts/pageContext'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import handleCallbackAction from '@functions/handleCallbackAction'

import useReporting from '@hooks/useReporting'

import type { PublicationBookingModel, PublicationOrderModel } from '@models/types'

import ManagePublicationBookingModal from '@modals/ManagePublicationBookingModal'
import MessageBookingContactsModal from '@modals/MessageBookingContactsModal'
import RevertPublicationOrderModal from '@modals/RevertPublicationOrderModal'

import PageContent from './components/PageContent'

const defaultState = {
  showManagePublicationBookingModal: false,
  showMessageBookingContactsModal: false,
  showRevertPublicationOrderModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    ManagePublicationBookingModal: {
      closeModal: () => setState({ showManagePublicationBookingModal: false }),
      ftpSync: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updatePublicationBooking: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    MessageBookingContactsModal: {
      closeModal: () => setState({ showMessageBookingContactsModal: false }),
      sendUnapprovedArtworkMessage: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    RevertPublicationOrderModal: {
      closeModal: () => setState({ showRevertPublicationOrderModal: false }),
      revertPublicationOrder: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }
  return componentCallbacks[component] || {}
}

interface ListProps {
  pageFilters: {
    [key: string]: any,
  },
}

const List = (props: ListProps) => {
  const { pageFilters } = props

  const [state, setState] = useSetState(defaultState)
  const {
    showManagePublicationBookingModal,
    showMessageBookingContactsModal,
    showRevertPublicationOrderModal,
  } = state

  const reportingPayload = useReporting({
    allowedReports: ['artwork_status'],
  })

  const modalContext = useModals({ setState })

  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(() => ({
    callbacks: {
      showManagePublicationBookingModal: (payload: { publicationBooking: PublicationBookingModel }) => {
        setModalData('ManagePublicationBookingModal', payload)
        setState({ showManagePublicationBookingModal: true })
      },
      showMessageBookingContactsModal: (payload: { publicationBooking: PublicationBookingModel }) => {
        setModalData('MessageBookingContactsModal', payload)
        setState({ showMessageBookingContactsModal: true })
      },
      showRevertPublicationOrderModal: (payload: { publicationOrder: PublicationOrderModel }) => {
        setModalData('RevertPublicationOrderModal', payload)
        setState({ showRevertPublicationOrderModal: true })
      },
    },
    reportingPayload,
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent pageFilters={pageFilters} />

        <ManagePublicationBookingModal
          // @ts-expect-error TS2322
          callbacks={callbacks('ManagePublicationBookingModal', setState)}
          showModal={showManagePublicationBookingModal}
        />

        <MessageBookingContactsModal
          // @ts-expect-error TS2322
          callbacks={callbacks('MessageBookingContactsModal', setState)}
          showModal={showMessageBookingContactsModal}
        />

        <RevertPublicationOrderModal
          // @ts-expect-error TS2322
          callbacks={callbacks('RevertPublicationOrderModal', setState)}
          showModal={showRevertPublicationOrderModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
